import { API_CONFIG, LIVEAVATAR_CONFIG } from '../constants';
import { apiFetch } from './apiFetch';
import { getAccessToken } from './auth';
import { tokenStorage } from './tokenStorage';
import { logSecretly } from './logSecretly';

/**
 * Calculate age from date of birth
 * @param {string} dateOfBirth - Date of birth string (e.g., "1990-01-15")
 * @returns {number} Age in years
 */
const calculateAge = (dateOfBirth) => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

/**
 * Legacy API request function - now uses new token system
 * @deprecated Consider using apiFetch directly for better token management
 */
export const apiRequest = async (endpoint, options = {}) => {
  const url = `${process.env.REACT_APP_BASE_URL?.replace(/=+$/, '')}${endpoint}`;

  try {
    // Use new apiFetch with automatic token management
    const response = await apiFetch({
      url: endpoint,
      method: options.method || 'GET',
      body: options.body,
      headers: options.headers,
      locale: options.locale || localStorage.getItem('app-language') || 'en'
    });

    if (!response.ok) {
      // Handle specific error cases
      if (response.status === 404) {
        console.warn(`API endpoint not found: ${url}`);
        throw new Error(`Endpoint not found: ${endpoint}`);
      } else if (response.status === 401) {
        console.warn(`Unauthorized access to: ${url}`);
        throw new Error(`Unauthorized access to: ${endpoint}`);
      } else if (response.status >= 500) {
        console.warn(`Server error for: ${url}`);
        throw new Error(`Server error: ${response.status}`);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

export const podApiRequest = async (endpoint, options = {}) => {
  const baseUrl = process.env.REACT_APP_BASE_URL_POD?.replace(/=+$/, '') || '';
  const fullUrl = baseUrl ? `${baseUrl}${endpoint}` : endpoint;

  try {
    // Use new apiFetch with automatic token management
    const response = await apiFetch({
      url: fullUrl,
      method: options.method || 'GET',
      body: options.body,
      headers: options.headers,
      locale: options.locale || localStorage.getItem('app-language') || 'en'
    });

    if (!response.ok) {
      // Handle specific error cases
      if (response.status === 404) {
        console.warn(`Pod API endpoint not found: ${fullUrl}`);
        throw new Error(`Pod endpoint not found: ${endpoint}`);
      } else if (response.status === 401) {
        console.warn(`Unauthorized access to pod: ${fullUrl}`);
        throw new Error(`Unauthorized access to pod: ${endpoint}`);
      } else if (response.status >= 500) {
        console.warn(`Pod server error for: ${fullUrl}`);
        throw new Error(`Pod server error: ${response.status}`);
      } else {
        throw new Error(`Pod HTTP error! status: ${response.status}`);
      }
    }

    return await response.json();
  } catch (error) {
    console.error('Pod API request failed:', error);
    throw error;
  }
};


/**
 * Transform profile data from the sign-in response into the internal patient data format
 * @param {Object} profileData - The full sign-in response containing user_info and pod_checkin_response
 * @returns {Object|null} - Formatted patient data or null if profileData is invalid
 */
export const getPatientDataFromProfile = (profileData) => {
  if (!profileData || !profileData.user_info) {
    return null;
  }

  const userInfo = profileData.user_info;
  const age = userInfo.date_of_birth
    ? calculateAge(userInfo.date_of_birth)
    : null;

  // Extract pod checkin info if available
  const userProfile = profileData.user_profile || {};
  const podInfo = profileData.pod_checkin_response || {};
  const patientId = podInfo.patientId || userInfo.user_id || userProfile.user_profile_id?.userId;

  return {
    id: patientId,
    name: userInfo.full_name || `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim() || '',
    age: age ?? 30,
    gender: userInfo.gender || '',
    email: userInfo.email || '',
    patientId: patientId,
    sessionId: podInfo.sessionId?.toString() || userInfo.session_id?.toString() || '',
    requestId: podInfo.requestId?.toString() || userInfo.request_id?.toString() || '',
    podIP: userInfo.podIP || userInfo.pod_ip || null,
    // Include additional fields
    firstName: userInfo.first_name || '',
    lastName: userInfo.last_name || '',
    mobile: userInfo.mobile || null,
    subjectId: userInfo.subject_id || null,
    dateOfBirth: userInfo.date_of_birth || null,
    membershipLevel: userInfo.membership_level || null,
    userGroups: userInfo.user_groups || [],
    doctor: userInfo.doctor || null,
    documents: userInfo.documents || []
  };
};

/**
 * Fetch current patient data from storage
 * @returns {Promise<Object|null>} - Patient data
 */
export const fetchCurrentPatient = async () => {
  const profileData = tokenStorage.getUserProfile();
  return getPatientDataFromProfile(profileData);
};

/**
 * Submit vitals data to API
 * @param {Object} vitalsData - Vitals measurement data
 * @returns {Promise<Object>} - API response
 */
export const submitVitalsData = async (vitalsData) => {
  const response = await apiFetch({
    url: API_CONFIG.ENDPOINTS.VITALS,
    method: 'POST',
    body: vitalsData
  });

  return await response.json();
};



/**
 * Fetch questionnaire data from API
 * @param {string} type - Assessment type (WELLNESS, LIFESTYLE, HEALTH_ASSESSMENT)
 * @returns {Promise<Array>} - Questionnaire data
 */
export const fetchQuestionnaire = async (type) => {
  try {
    // Use apiFetch which handles token automatically
    const response = await apiFetch({
      url: API_CONFIG.ENDPOINTS.QUESTIONNAIRE,
      method: 'GET',
      params: { type },
      locale: localStorage.getItem('app-language') || 'en'
    });

    return await response.json();
  } catch (error) {
    console.error('Error fetching questionnaire:', error);
    throw error;
  }
};

/**
 * Fetch pod data from API by IP address
 * @param {string} ipAddress - IP address to fetch pod data for
 * @returns {Promise<Object>} - Pod data
 */
export const fetchPodData = async (ipAddress) => {
  try {
    // Use apiFetch which handles token automatically
    const response = await apiFetch({
      url: API_CONFIG.ENDPOINTS.POD_IP(ipAddress),
      method: 'GET'
    });

    return await response.json();
  } catch (error) {
    console.error('Error fetching pod data:', error);
    throw error;
  }
};

export const submitQuestionnaire = async (submissionData) => {
  try {
    // Use apiFetch which handles token automatically
    const response = await apiFetch({
      url: API_CONFIG.ENDPOINTS.SUBMIT_QUESTIONNAIRE,
      method: 'POST',
      params: { source: 'POD' },
      body: submissionData,
      locale: localStorage.getItem('app-language') || 'en'
    });

    return await response.json();
  } catch (error) {
    console.error('Error submitting questionnaire:', error);
    throw error;
  }
};

export const sendConsultationEmails = async (podIp, patientId) => {
  try {
    // Use apiFetch which handles token automatically
    const response = await apiFetch({
      url: API_CONFIG.ENDPOINTS.POD_CONSULTATION_EMAILS,
      method: 'POST',
      body: {
        podIp,
        patientId
      }
    });

    return await response.json();
  } catch (error) {
    console.error('Error sending consultation emails:', error);
    throw error;
  }
};


export const endSession = async (podIP, sessionId) => {
  try {
    // Use apiFetch which handles token automatically
    const response = await apiFetch({
      url: `/mt-vmr/pod/${podIP}/checkout`,
      method: 'POST',
      body: {
        sessionId: sessionId
      }
    });

    return await response.json();
  } catch (error) {
    console.error('Error ending session:', error);
    throw error;
  }
};

/**
 * Fetch pod schedule/availability
 * @param {number} podId - Pod ID (default: 1)
 * @param {number} componentId - Component ID (default: 4)
 * @param {number} familyId - Family ID (default: 1)
 * @param {number} storefrontId - Storefront ID (default: 1)
 * @returns {Promise<Object>} - Pod schedule data
 */
export const fetchPodSchedule = async (podId = 1, componentId = 4, familyId = 1, storefrontId = 1) => {
  try {
    const response = await apiFetch({
      url: `/mt-vmr/pod/${podId}/schedule`,
      method: 'GET',
      params: {
        component_id: componentId,
        family_id: familyId,
        storefront_id: storefrontId
      },
      headers: {
        'x-family-id': familyId.toString()
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch pod schedule: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching pod schedule:', error);
    throw error;
  }
};

/**
 * Sign in to the backend system after Keycloak authentication
 * @returns {Promise<Object>} - API response
 */
export const signInToSystem = async () => {
  try {
    // Use apiFetch which handles token automatically
    const response = await apiFetch({
      url: '/mt-account/auth/sign-in',
      method: 'GET',
      params: {
        component_id: 5,
        family_id: 1,
        storefront_id: 1
      }
    });

    // Check if response is OK
    if (!response.ok) {
      // Handle specific error status codes
      if (response.status === 401 || response.status === 403) {
        throw new Error('Unauthorized: Sign-in failed');
      } else if (response.status >= 500) {
        throw new Error('Server error during sign-in');
      } else {
        throw new Error(`Sign-in failed with status: ${response.status}`);
      }
    }

    return await response.json();
  } catch (error) {
    console.error('System sign-in failed:', error);
    throw error;
  }
};

export const avatarApiRequest = async (endpoint, options = {}) => {
  const url = `${process.env.REACT_APP_BASE_URL_AI_ASSISTANT?.replace(/=+$/, '')}${endpoint}`;

  const isFormData = options.body instanceof FormData;
  const defaultOptions = {
    headers: isFormData ? {} : {
      'Content-Type': 'application/json',
    },
  };

  const config = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Avatar API request failed:', error);
    throw error;
  }
};

export const createSession = async (language, gender) => {
  try {
    const authToken = await getAccessToken();
    const url = `${process.env.REACT_APP_BASE_URL_AI_ASSISTANT?.replace(/=+$/, '')}/api/v1/sessions`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        language: language,
        gender: gender
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get('content-type');

    // Check if response is audio
    if (contentType && contentType.includes('audio/')) {
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      // Also try to get session_id from headers if available
      const sessionId = response.headers.get('session-id') || response.headers.get('x-session-id');

      return {
        session_id: sessionId,
        audio: audioUrl,
        audioBlob: audioBlob
      };
    }

    // Otherwise, parse as JSON
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating session:', error);
    throw error;
  }
};

export const processAudio = async (sessionId, audioFile) => {
  try {
    const authToken = await getAccessToken();
    const url = `${process.env.REACT_APP_BASE_URL_AI_ASSISTANT?.replace(/=+$/, '')}/api/v1/audio?session_id=${sessionId}`;
    const formData = new FormData();
    formData.append('audio', audioFile, 'recording.webm');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get('content-type');

    // Check if response is audio
    if (contentType && contentType.includes('audio/')) {
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      return {
        audio: audioUrl,
        audioBlob: audioBlob
      };
    }

    // Otherwise, parse as JSON
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error processing audio:', error);
    throw error;
  }
};

export const processText = async (sessionId, command) => {
  try {
    const authToken = await getAccessToken();
    const url = `${process.env.REACT_APP_BASE_URL_AI_ASSISTANT?.replace(/=+$/, '')}/api/v1/text?session_id=${sessionId}&question_type=${command}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get('content-type');

    // Check if response is audio
    if (contentType && contentType.includes('audio/')) {
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      return {
        audio: audioUrl,
        audioBlob: audioBlob
      };
    }

    // Otherwise, parse as JSON
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error processing text:', error);
    throw error;
  }
};

/**
 * LiveAvatar API Functions
 * Note: In production, these should be proxied through your backend to keep API keys secure
 */

/**
 * Create a LiveAvatar session token
 * @param {string} language - Language code (e.g., 'en', 'ar')
 * @param {string} mode - Session mode ('FULL' or 'LITE')
 * @returns {Promise<Object>} - Session token data
 */
export const createLiveAvatarSessionToken = async (language = 'en', mode = 'FULL') => {
  try {
    const url = `${LIVEAVATAR_CONFIG.API_URL}/sessions/token`;
    
    const requestBody = {
      mode: mode,
      avatar_id: LIVEAVATAR_CONFIG.AVATAR_ID,
      avatar_persona: {
        voice_id: LIVEAVATAR_CONFIG.VOICE_ID,
        context_id: LIVEAVATAR_CONFIG.CONTEXT_ID,
        language: language
      }
    };
    
    logSecretly('🔍 [API] Creating LiveAvatar token with:', {
      url,
      has_api_key: !!LIVEAVATAR_CONFIG.API_KEY,
      api_key_length: LIVEAVATAR_CONFIG.API_KEY?.length,
      request_body: requestBody
    });
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'X-API-KEY': LIVEAVATAR_CONFIG.API_KEY,
        'accept': 'application/json',
        'content-type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [API] LiveAvatar token request failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText
      });
      throw new Error(`Failed to create LiveAvatar session token: ${response.status}`);
    }

    const data = await response.json();
    
    logSecretly('🔍 [API] LiveAvatar token response:', {
      code: data.code,
      message: data.message,
      has_session_id: !!data.data?.session_id,
      has_session_token: !!data.data?.session_token
    });
    
    if (data.code === 1000 && data.data) {
      return {
        session_id: data.data.session_id,
        session_token: data.data.session_token
      };
    } else {
      console.error('❌ [API] Invalid token response:', data);
      throw new Error(data.message || 'Failed to create session token');
    }
  } catch (error) {
    console.error('Error creating LiveAvatar session token:', error);
    throw error;
  }
};

/**
 * Stop a LiveAvatar session
 * @param {string} sessionToken - Session token from createLiveAvatarSessionToken
 * @param {string} sessionId - Session ID from createLiveAvatarSessionToken
 * @returns {Promise<Object>} - Stop session response
 */
export const stopLiveAvatarSession = async (sessionToken, sessionId) => {
  try {
    const url = `${LIVEAVATAR_CONFIG.API_URL}/sessions/stop`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`
      },
      body: JSON.stringify({
        session_id: sessionId
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to stop LiveAvatar session: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error stopping LiveAvatar session:', error);
    throw error;
  }
};
